import {
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  doc,
  deleteDoc
} from 'firebase/firestore';
import { ref, listAll, deleteObject } from 'firebase/storage';
import { deleteUser } from 'firebase/auth';
import { db, auth, storage } from './firebase';

export async function deleteUserAccount(): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user');

  const userId = user.uid;
  const userEmail = user.email || '';

  try {
    console.log('Starting account deletion for user:', userId);

    const batch = writeBatch(db);
    let batchCount = 0;
    const MAX_BATCH_SIZE = 500;

    const commitBatchIfNeeded = async () => {
      if (batchCount >= MAX_BATCH_SIZE) {
        await batch.commit();
        batchCount = 0;
      }
    };

    // 1. Delete user's starters
    console.log('Deleting starters...');
    const startersRef = collection(db, 'users', userId, 'starters');
    const startersSnapshot = await getDocs(startersRef);
    for (const starterDoc of startersSnapshot.docs) {
      batch.delete(starterDoc.ref);
      batchCount++;
      await commitBatchIfNeeded();
    }

    // 2. Delete user's baking sessions
    console.log('Deleting baking sessions...');
    const sessionsRef = collection(db, 'users', userId, 'bakingSessions');
    const sessionsSnapshot = await getDocs(sessionsRef);
    for (const sessionDoc of sessionsSnapshot.docs) {
      batch.delete(sessionDoc.ref);
      batchCount++;
      await commitBatchIfNeeded();
    }

    // 3. Delete user's challenges
    console.log('Deleting user challenges...');
    const userChallengesRef = collection(db, 'users', userId, 'challenges');
    const userChallengesSnapshot = await getDocs(userChallengesRef);
    for (const challengeDoc of userChallengesSnapshot.docs) {
      batch.delete(challengeDoc.ref);
      batchCount++;
      await commitBatchIfNeeded();
    }

    // 4. Delete user's analyses
    console.log('Deleting analyses...');
    const analysesRef = collection(db, 'users', userId, 'analyses');
    const analysesSnapshot = await getDocs(analysesRef);
    for (const analysisDoc of analysesSnapshot.docs) {
      batch.delete(analysisDoc.ref);
      batchCount++;
      await commitBatchIfNeeded();
    }

    // 5. Delete user's notification settings
    console.log('Deleting notification settings...');
    const settingsRef = collection(db, 'users', userId, 'settings');
    const settingsSnapshot = await getDocs(settingsRef);
    for (const settingDoc of settingsSnapshot.docs) {
      batch.delete(settingDoc.ref);
      batchCount++;
      await commitBatchIfNeeded();
    }

    // Commit any remaining batch operations
    if (batchCount > 0) {
      await batch.commit();
      batchCount = 0;
    }

    // 6. Delete user's notifications
    console.log('Deleting notifications...');
    const notificationsRef = collection(db, 'notifications');
    const notificationsQuery = query(notificationsRef, where('userId', '==', userId));
    const notificationsSnapshot = await getDocs(notificationsQuery);

    const notificationsBatch = writeBatch(db);
    let notificationsBatchCount = 0;
    for (const notificationDoc of notificationsSnapshot.docs) {
      notificationsBatch.delete(notificationDoc.ref);
      notificationsBatchCount++;
      if (notificationsBatchCount >= MAX_BATCH_SIZE) {
        await notificationsBatch.commit();
        notificationsBatchCount = 0;
      }
    }
    if (notificationsBatchCount > 0) {
      await notificationsBatch.commit();
    }

    // 7. Delete content reports created by the user
    console.log('Deleting content reports...');
    const reportsRef = collection(db, 'contentReports');
    const reportsQuery = query(reportsRef, where('reportedBy.userId', '==', userId));
    const reportsSnapshot = await getDocs(reportsQuery);

    const reportsBatch = writeBatch(db);
    let reportsBatchCount = 0;
    for (const reportDoc of reportsSnapshot.docs) {
      reportsBatch.delete(reportDoc.ref);
      reportsBatchCount++;
      if (reportsBatchCount >= MAX_BATCH_SIZE) {
        await reportsBatch.commit();
        reportsBatchCount = 0;
      }
    }
    if (reportsBatchCount > 0) {
      await reportsBatch.commit();
    }

    // 8. Remove user from all challenge participants
    console.log('Removing from challenges...');
    const challengesRef = collection(db, 'challenges');
    const challengesSnapshot = await getDocs(challengesRef);

    const challengesBatch = writeBatch(db);
    let challengesBatchCount = 0;
    for (const challengeDoc of challengesSnapshot.docs) {
      const challengeData = challengeDoc.data();
      if (challengeData.participants && Array.isArray(challengeData.participants)) {
        const filteredParticipants = challengeData.participants.filter(
          (p: any) => p.userId !== userId
        );
        if (filteredParticipants.length !== challengeData.participants.length) {
          challengesBatch.update(challengeDoc.ref, { participants: filteredParticipants });
          challengesBatchCount++;
          if (challengesBatchCount >= MAX_BATCH_SIZE) {
            await challengesBatch.commit();
            challengesBatchCount = 0;
          }
        }
      }
    }
    if (challengesBatchCount > 0) {
      await challengesBatch.commit();
    }

    // 9. Delete storage files
    console.log('Deleting storage files...');
    try {
      const userStorageRef = ref(storage, `users/${userId}`);
      const storageList = await listAll(userStorageRef);

      const deletePromises = storageList.items.map(item => deleteObject(item));

      for (const prefixRef of storageList.prefixes) {
        const subList = await listAll(prefixRef);
        deletePromises.push(...subList.items.map(item => deleteObject(item)));
      }

      await Promise.all(deletePromises);
    } catch (storageError) {
      console.warn('Error deleting storage files:', storageError);
    }

    // 10. Delete user document
    console.log('Deleting user document...');
    const userDocRef = doc(db, 'users', userId);
    await deleteDoc(userDocRef);

    // 11. Delete Firebase Auth account
    console.log('Deleting Firebase Auth account...');
    await deleteUser(user);

    console.log('Account deletion completed successfully');

    return;
  } catch (error) {
    console.error('Error deleting user account:', error);
    throw error;
  }
}
